// The job number of a project ("Jobnummer"): the field in the New project
// dialog and on the Details page (components/alice/JobNumberPicker.vue), and
// what the searchProductiveJobs and setProjectJobNumber callables
// (functions/modules/job-number) return. Pure, so it is unit-tested in
// tests/unit/jobNumber.test.js.

const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");

// The format the server takes (functions/modules/job-number/jobNumber.js,
// parseJobNumber): digits with an optional D, B or P before them and an
// optional .N after them. Loose on purpose: "d 2303", "D-2303" and "#2303" are
// fine.
const JOB_RE = /^#?\s*([DBP])?\s*[-–]?\s*(\d{1,5})(?:\.(\d{1,3}))?$/i;

export const JOB_FORMAT_HINT =
  "Gebruik een jobnummer zoals D2303, 2386 of 2055.1.";

// "d 02303" -> "D2303"; null for an empty or malformed one.
export function normaliseJobNumber(value) {
  const raw = text(value);
  const m = raw.match(JOB_RE);
  if (!m) return null;
  const prefix = m[1] ? m[1].toUpperCase() : "";
  const number = Number(m[2]);
  const suffix = m[3] === undefined ? null : Number(m[3]);
  if (!number || suffix === 0) return null;
  return `${prefix}${number}${suffix ? `.${suffix}` : ""}`;
}

// The field's error: none when it is empty (the job number is optional) or
// valid.
export function jobNumberError(value) {
  if (!text(value)) return "";
  return normaliseJobNumber(value) ? "" : JOB_FORMAT_HINT;
}

const MONTHS = [
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

// "2026-09-14" -> "sep 2026".
export function monthLabel(iso) {
  const m = text(iso).match(/^(\d{4})-(\d{2})/);
  if (!m) return "";
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${m[1]}` : "";
}

// One row of the list: "D2303 · OHRA autoverzekering · sep 2026".
export function jobRowLabel(row) {
  if (!row) return "";
  return [text(row.number), text(row.name), monthLabel(row.date)]
    .filter(Boolean)
    .join(" · ");
}

// The line under it: the company when it is not the client's own job, and
// whether the job has a Drive project folder to link.
export function jobRowDetail(row) {
  if (!row) return "";
  return [
    row.other_client && text(row.company_name)
      ? `${text(row.company_name)} (andere klant)`
      : "",
    row.has_drive_folder ? "Drive-map gevonden" : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

// What the Details page says under a saved job number: the Productive job it
// names.
export function productiveRefLabel(ref) {
  const name = ref && text(ref.name);
  return name ? `Productive: ${name}` : "";
}

// The Productive key to send with a job number: only the one of the row the
// colleague picked, and only while the field still holds that row's number.
export function pickedKey(row, value) {
  return row && text(row.key) && normaliseJobNumber(value) === row.number
    ? row.key
    : null;
}

// setProjectJobNumber's errors, in words a colleague can act on.
export function jobNumberErrorMessage(err) {
  const code = String((err && err.code) || "").replace(/^functions\//, "");
  const message = String((err && err.message) || "");
  if (/job number is digits/i.test(message)) return JOB_FORMAT_HINT;
  if (code === "permission-denied" || code === "unauthenticated") {
    return "Alleen het Valsplat-team kan het jobnummer opslaan.";
  }
  if (code === "not-found") return "Dit project is niet gevonden.";
  return "Het jobnummer kon niet worden opgeslagen. Probeer het opnieuw.";
}
